import {
  QAlert,
  QButton,
  QIcon,
  QLink,
  QSpinner,
  QStack,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  fetchUsergroupPermissions,
  getCompanyAdminSettings,
  patchCompanyAdminSettings,
  updateUsergroupPermission,
} from '../../../api/medtech-api';
import { MedtechRole, MedtechRolesMap } from '../../../types/medtechRole.types';
import { CanReviewDocuments } from '../../../utils/validation/medtechPermissions';
import { PermissionControl } from './PermissionsControl';
import { ManageableUsergroupPermission, ManageableUsergroupPermissionsByRoleMap } from './types';

export const Permissions = () => {
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const isBasicUsersReviewDocsCapabilityEnabled = currentUser.capabilities?.basic_users_review_docs;
  const isAiCapabilityEnabled = currentUser.capabilities?.ai === true;
  const isMcpCapabilityEnabled = currentUser.capabilities?.mcp === true;
  const isCopilotCapabilityEnabled = currentUser.capabilities?.copilot === true;
  const isMcpToggleVisible = isMcpCapabilityEnabled;
  const isCopilotToggleVisible = isCopilotCapabilityEnabled;
  const [isLoading, setLoading] = useState(true);
  const [isGhostEnabled, setIsGhostEnabled] = useState(false);
  const [aiEnabledValue, setAiEnabledValue] = useState(false);
  const [mcpEnabledValue, setMcpEnabledValue] = useState(false);
  const [copilotEnabledValue, setCopilotEnabledValue] = useState(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [permissions, setPermissions] = useState<ManageableUsergroupPermissionsByRoleMap>({
    [MedtechRolesMap.basic]: {
      [CanReviewDocuments]: false,
    },
  });

  useEffect(() => {
    const fetchAdminSettings = async () => {
      setLoading(true);
      try {
        const response = await getCompanyAdminSettings(currentUser.companyId);
        setAiEnabledValue(response.data.ai_enabled);
        setIsGhostEnabled(response.data.allow_ghost);
        setMcpEnabledValue(response.data.mcp_enabled ?? false);
        setCopilotEnabledValue(response.data.copilot_enabled ?? false);
      } catch (error) {
        showToast({
          'data-cy': 'permissions-load-error-toast',
          id: 'get-admin-settings',
          replace: true,
          title: t('common.error'),
          description: t('messages.error.loadSettings'),
          status: 'error',
        });
      }
      setLoading(false);
    };

    fetchAdminSettings();
  }, [currentUser.companyId, showToast]);

  const getUsergroupPermissions = () => {
    return fetchUsergroupPermissions(currentUser.companyId).then((data) => {
      setPermissions((currentValue) => {
        return {
          ...currentValue,
          [MedtechRolesMap.basic]: {
            [CanReviewDocuments]: data[MedtechRolesMap.basic].some(
              (permission) => permission.permission_id === CanReviewDocuments,
            ),
          },
        };
      });
      return data;
    });
  };

  const { isLoading: isLoadingUsergroupPermissions } = useQuery({
    queryKey: ['usergroup-permissions', currentUser.companyId],
    queryFn: getUsergroupPermissions,
    refetchOnWindowFocus: false,
    enabled: isBasicUsersReviewDocsCapabilityEnabled,
  });

  if (isLoading || (isBasicUsersReviewDocsCapabilityEnabled && isLoadingUsergroupPermissions)) {
    return <QSpinner size="sm" />;
  }

  const updateGhostValue = (allowGhost: boolean) => {
    return patchCompanyAdminSettings(currentUser.companyId, { allow_ghost: allowGhost });
  };

  const updateCompanyAiEnabled = (aiEnabled: boolean) => {
    return patchCompanyAdminSettings(currentUser.companyId, { ai_enabled: aiEnabled });
  };

  const updateCompanyMcpEnabled = (mcpEnabled: boolean) => {
    return patchCompanyAdminSettings(currentUser.companyId, { mcp_enabled: mcpEnabled });
  };

  const updateCompanyCopilotEnabled = (copilotEnabled: boolean) => {
    return patchCompanyAdminSettings(currentUser.companyId, { copilot_enabled: copilotEnabled });
  };

  const updateUsergroupPermissions = () => {
    const updates = Object.entries(permissions).reduce((acc: Promise<void>[], [usergroup, usergroupPermissionsMap]) => {
      const usergroupPermissionUpdates = Object.entries(usergroupPermissionsMap).reduce(
        (all: Promise<void>[], [permission, isEnabled]) => {
          return all.concat(
            updateUsergroupPermission(
              currentUser.companyId,
              usergroup as MedtechRole,
              permission as ManageableUsergroupPermission,
              isEnabled,
            ),
          );
        },
        [],
      );
      return [...acc, ...usergroupPermissionUpdates];
    }, []);
    return Promise.all(updates);
  };

  const onApplyChanges = async () => {
    setIsApplyingChanges(true);
    const updates: Promise<any>[] = [updateGhostValue(isGhostEnabled)];

    if (isBasicUsersReviewDocsCapabilityEnabled) {
      updates.push(updateUsergroupPermissions());
    }
    if (isAiCapabilityEnabled) {
      updates.push(updateCompanyAiEnabled(aiEnabledValue));
    }
    if (isMcpToggleVisible) {
      updates.push(updateCompanyMcpEnabled(mcpEnabledValue));
    }
    if (isCopilotToggleVisible) {
      updates.push(updateCompanyCopilotEnabled(copilotEnabledValue));
    }

    await Promise.all(updates)
      .then(() => {
        showToast({
          'data-cy': 'permissions-update-success-toast',
          id: 'update-permissions',
          replace: true,
          title: t('permissions.instancePermissions'),
          description: t('messages.success.permissionsUpdated'),
          status: 'success',
        });
      })
      .catch(() => {
        showToast({
          'data-cy': 'permissions-update-error-toast',
          id: 'update-permissions',
          replace: true,
          title: t('permissions.instancePermissions'),
          description: t('messages.error.updatePermissions'),
          status: 'error',
        });
      });
    setIsApplyingChanges(false);
  };

  const handleGhostCheckboxChange = (configId: string, enabled: boolean) => {
    setIsGhostEnabled(enabled);
  };

  const handleAiCheckboxChange = (configId: string, enabled: boolean) => {
    setAiEnabledValue(enabled);
  };

  const handleMcpCheckboxChange = (configId: string, enabled: boolean) => {
    setMcpEnabledValue(enabled);
  };

  const handleCopilotCheckboxChange = (configId: string, enabled: boolean) => {
    setCopilotEnabledValue(enabled);
  };

  const handleUsergroupPermissionCheckboxChange = (
    usergroup: keyof ManageableUsergroupPermissionsByRoleMap,
    permission: string,
    isEnabled: boolean,
  ) => {
    setPermissions((currentValue) => {
      return {
        ...currentValue,
        [usergroup]: {
          ...currentValue[usergroup],
          [permission]: isEnabled,
        },
      };
    });
  };

  const showAiDisclaimer = isAiCapabilityEnabled || isCopilotToggleVisible;

  return (
    <QStack spacing="8">
      {showAiDisclaimer && (
        <QAlert
          status="info"
          data-cy="tos-alert-permissions"
          isInline
          description={
            <QText data-cy="tos-text-permissions">
              {t('permissions.termsOfService.prefix')} <QIcon iconName="QualioAI" size="sm" />{' '}
              {t('permissions.termsOfService.tos')}{' '}
              <QLink href="https://www.qualio.com/docs/terms" isExternal>
                {t('common.learnMore')}
              </QLink>{' '}
              {t('permissions.termsOfService.fairUsage')}
            </QText>
          }
        />
      )}
      {isAiCapabilityEnabled && (
        <QStack>
          <PermissionControl
            label={t('permissions.aiEditing.label')}
            description={t('permissions.aiEditing.description')}
            permissionId="ai_enabled"
            permissionLabel={t('permissions.aiEditing.enable')}
            isChecked={aiEnabledValue}
            handleChange={handleAiCheckboxChange}
            data-cy="permissions-ai-checkbox"
          />
        </QStack>
      )}
      {isCopilotToggleVisible && (
        <QStack>
          <PermissionControl
            label={t('permissions.copilot.label')}
            description={t('permissions.copilot.description')}
            permissionId="copilot_enabled"
            permissionLabel={t('permissions.copilot.enable')}
            isChecked={copilotEnabledValue}
            handleChange={handleCopilotCheckboxChange}
            data-cy="permissions-copilot-checkbox"
          />
        </QStack>
      )}
      {isMcpToggleVisible && (
        <QStack>
          <PermissionControl
            label={t('permissions.mcp.label')}
            description={t('permissions.mcp.description')}
            permissionId="mcp_enabled"
            permissionLabel={t('permissions.mcp.enable')}
            isChecked={mcpEnabledValue}
            handleChange={handleMcpCheckboxChange}
            data-cy="permissions-mcp-checkbox"
          />
        </QStack>
      )}
      {isBasicUsersReviewDocsCapabilityEnabled && (
        <QStack>
          <PermissionControl
            label={t('permissions.reviewers.label')}
            description={t('permissions.reviewers.description')}
            permissionId={CanReviewDocuments}
            permissionLabel={t('permissions.reviewers.enable')}
            isChecked={permissions[MedtechRolesMap.basic][CanReviewDocuments]}
            handleChange={(_configId, enabled) =>
              handleUsergroupPermissionCheckboxChange(MedtechRolesMap.basic, CanReviewDocuments, enabled)
            }
            data-cy="permissions-basic-reviewers-checkbox"
          />
        </QStack>
      )}
      <PermissionControl
        label={t('permissions.supportAccess.label')}
        description={t('permissions.supportAccess.description')}
        data-cy="instance-access-checkbox"
        permissionId="allow_ghost"
        permissionLabel={t('permissions.supportAccess.enable')}
        isChecked={isGhostEnabled}
        handleChange={handleGhostCheckboxChange}
      />
      <QButton isLoading={isApplyingChanges} onClick={onApplyChanges} data-cy="save-changes">
        {t('permissions.saveChanges')}
      </QButton>
    </QStack>
  );
};
